import type { ApiPaymentRecord } from "./hooks/usePaymentRecords";
import type {
  TaxDeductionProfile,
  TaxDeductionReview,
} from "./hooks/useTaxDeductions";
import { parseDecimalAmount } from "./decimalAmount";
import {
  calculateMedicalExpenseDeduction,
  deriveMedicalExpenseCandidates,
  type MedicalExpenseCandidate,
  type MedicalExpenseReviewStatus,
} from "./medicalExpenseDeduction";

export type TaxReviewMissingReason =
  | "payment_document"
  | "insurance_outcome"
  | "amount_or_date"
  | "provider"
  | "eligibility_context"
  | "not_medical_expense";

export type TaxReviewEntry = {
  candidate: MedicalExpenseCandidate;
  record: ApiPaymentRecord;
  reviewStatus: MedicalExpenseReviewStatus;
  attachedDocumentCount: number;
  missingReasons: TaxReviewMissingReason[];
};

export type TaxReviewSummary = {
  taxYear: number;
  recordsAnalyzed: number;
  readyCount: number;
  needsAttentionCount: number;
  patientPaidTotal: number;
  calculation: ReturnType<typeof calculateMedicalExpenseDeduction>;
  entries: TaxReviewEntry[];
  profile: TaxDeductionProfile | null;
};

function isTaxCandidateRecord(record: ApiPaymentRecord, taxYear: number) {
  return (
    (record.kind === "payment" || record.kind === "receipt") &&
    record.occurredOn?.startsWith(`${taxYear}-`)
  );
}

function attachedDocumentCount(record: ApiPaymentRecord) {
  return (record.documents ?? []).filter(
    (document) => document.status === "attached",
  ).length;
}

function missingReasonsForRecord(
  record: ApiPaymentRecord,
  candidate: MedicalExpenseCandidate,
) {
  const missingReasons: TaxReviewMissingReason[] = [];
  const taxEligibility = record.taxEligibility;

  if (attachedDocumentCount(record) === 0) {
    missingReasons.push("payment_document");
  }

  if (!record.provider?.trim()) {
    missingReasons.push("provider");
  }

  if (
    record.status === "submitted" ||
    record.status === "waiting-eob" ||
    record.status === "partially-covered"
  ) {
    if (!taxEligibility || taxEligibility.insuranceOutcome === "unknown") {
      missingReasons.push("insurance_outcome");
    }
  }

  if (!record.occurredOn || candidate.patientPaidAmount <= 0) {
    missingReasons.push("amount_or_date");
  }

  // Use the structured payment-record eligibility fields for the local
  // fallback. The API calculation snapshot is authoritative when available;
  // this path is only used while it is loading or unavailable.
  if (
    !taxEligibility ||
    taxEligibility.expenseType === "unknown" ||
    taxEligibility.careRecipient === "unknown"
  ) {
    missingReasons.push("eligibility_context");
  }
  if (taxEligibility?.expenseType === "not_medical") {
    missingReasons.push("not_medical_expense");
  }

  return missingReasons;
}

export function buildTaxReviewSummary(
  records: ApiPaymentRecord[],
  reviews: TaxDeductionReview[],
  profile: TaxDeductionProfile | null,
  taxYear: number,
): TaxReviewSummary {
  const taxYearRecords = records.filter((record) =>
    isTaxCandidateRecord(record, taxYear),
  );
  const candidates = deriveMedicalExpenseCandidates(records, reviews, taxYear);
  const recordsById = new Map(
    taxYearRecords.map((record) => [record.id, record]),
  );
  const entries = candidates
    .map((candidate): TaxReviewEntry | null => {
      const record = recordsById.get(candidate.id);
      if (!record) return null;

      return {
        candidate,
        record,
        reviewStatus: candidate.reviewStatus,
        attachedDocumentCount: attachedDocumentCount(record),
        missingReasons: missingReasonsForRecord(record, candidate),
      };
    })
    .filter((entry): entry is TaxReviewEntry => entry !== null);
  const calculation = calculateMedicalExpenseDeduction(
    candidates,
    parseDecimalAmount(profile?.adjustedGrossIncome),
    profile?.itemizesDeductions,
  );

  return {
    taxYear,
    recordsAnalyzed: taxYearRecords.length,
    readyCount: entries.filter(
      (entry) =>
        entry.reviewStatus === "included" && entry.missingReasons.length === 0,
    ).length,
    needsAttentionCount: entries.filter(
      (entry) =>
        entry.reviewStatus === "needs-review" ||
        entry.missingReasons.length > 0,
    ).length,
    patientPaidTotal: entries
      .filter((entry) => entry.reviewStatus !== "excluded")
      .reduce((total, entry) => total + entry.candidate.patientPaidAmount, 0),
    calculation,
    entries,
    profile,
  };
}
